import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import api from '../../api';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';

const emptySection = (order) => ({
  isSection: true,
  sectionType: '',
  sectionTitle: '',
  sectionOrder: order,
  pointsWithOptions: []
});

const INPUT_TYPES = [
  { value: 'RADIO', label: 'Radio / Select (use options below)' },
  { value: 'NUMBER', label: 'Number input' },
  { value: 'TEXT', label: 'Text input' },
  { value: 'CURRENCY', label: 'Currency input' }
];

const emptyPoint = () => ({
  pointNumber: '',
  pointText: '',
  options: [],
  selectedOptionId: null,
  inputType: 'RADIO',
  inputSuffix: ''
});

const emptyOption = () => ({
  optionId: '',
  optionText: ''
});

/** Option keys are always derived from point number + position: e.g. "3.3" => "3.3-option-1", "3.3-option-2" */
const getAutoFillOptionKey = (pointNumber, optionIndex) =>
  pointNumber ? `${String(pointNumber).trim()}-option-${optionIndex + 1}` : `option-${optionIndex + 1}`;

const CommercialTermDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTemplateFromList = location.state?.template;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(() => ({
    id: initialTemplateFromList?.id || Number(id),
    code: initialTemplateFromList?.code || '',
    name: initialTemplateFromList?.name || '',
    description: initialTemplateFromList?.description || '',
    serviceId: initialTemplateFromList?.serviceId ?? null,
    proposalTemplateId: initialTemplateFromList?.proposalTemplateId ?? null
  }));
  const [sections, setSections] = useState([]);
  const [services, setServices] = useState([]);
  const [proposalTemplateOptions, setProposalTemplateOptions] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/md/commercial-terms/templates/${id}`);
        const data = res.data || {};
        setTemplate(prev => ({
          ...prev,
          code: data.id || prev.code,
          name: data.name || prev.name,
          description: data.description || prev.description,
          serviceId: data.serviceId ?? prev.serviceId,
          proposalTemplateId: data.proposalTemplateId ?? prev.proposalTemplateId
        }));
        const terms = Array.isArray(data.terms) ? data.terms : [];
        const normalizedSections = terms
          .filter(t => t.isSection)
          .map((sec, idx) => ({
            isSection: true,
            sectionType: sec.sectionType || '',
            sectionTitle: sec.sectionTitle || '',
            sectionOrder: sec.sectionOrder || idx + 1,
            pointsWithOptions: (sec.pointsWithOptions || []).map(p => {
              const pointNumber = p.pointNumber || '';
              const options = (p.options || []).map((o, i) => ({
                optionId: getAutoFillOptionKey(pointNumber, i),
                optionText: o.optionText || ''
              }));
              const hasOptions = (p.options || []).length > 0;
              return {
                pointNumber,
                pointText: p.pointText || '',
                options,
                selectedOptionId: p.selectedOptionId || null,
                inputType: p.inputType || (hasOptions ? 'RADIO' : 'TEXT'),
                inputSuffix: p.inputSuffix || ''
              };
            })
          }));
        setSections(normalizedSections.length > 0 ? normalizedSections : [emptySection(1)]);
      } catch (error) {
        console.error('Error loading commercial term template detail:', error);
        setFailMessage('Failed to load commercial term template detail.');
        setShowFailModal(true);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await api.get('/md/company-service');
        setServices(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        setServices([]);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const fetchProposalTemplates = async () => {
      try {
        const res = await api.get('/crm/proposal-templates');
        setProposalTemplateOptions(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        setProposalTemplateOptions([]);
      }
    };
    fetchProposalTemplates();
  }, []);

  const updateSection = (index, updater) => {
    setSections(prev => {
      const copy = [...prev];
      copy[index] = updater(copy[index]);
      return copy;
    });
  };

  const addSection = () => {
    setSections(prev => [...prev, emptySection(prev.length + 1)]);
  };

  const removeSection = (index) => {
    setSections(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((s, idx) => ({ ...s, sectionOrder: idx + 1 }));
    });
  };

  const addPoint = (sectionIndex) => {
    updateSection(sectionIndex, (sec) => ({
      ...sec,
      pointsWithOptions: [...(sec.pointsWithOptions || []), emptyPoint()]
    }));
  };

  const removePoint = (sectionIndex, pointIndex) => {
    updateSection(sectionIndex, (sec) => ({
      ...sec,
      pointsWithOptions: (sec.pointsWithOptions || []).filter((_, i) => i !== pointIndex)
    }));
  };

  const addOptionToPoint = (sectionIndex, pointIndex) => {
    updateSection(sectionIndex, (sec) => {
      const points = [...(sec.pointsWithOptions || [])];
      const point = { ...(points[pointIndex] || emptyPoint()) };
      const currentOptions = point.options || [];
      const newOption = {
        optionId: getAutoFillOptionKey(point.pointNumber, currentOptions.length),
        optionText: ''
      };
      point.options = [...currentOptions, newOption];
      points[pointIndex] = point;
      return { ...sec, pointsWithOptions: points };
    });
  };

  const removeOptionFromPoint = (sectionIndex, pointIndex, optionIndex) => {
    updateSection(sectionIndex, (sec) => {
      const points = [...(sec.pointsWithOptions || [])];
      const point = { ...(points[pointIndex] || emptyPoint()) };
      point.options = (point.options || []).filter((_, i) => i !== optionIndex);
      if (point.pointNumber) {
        point.options = point.options.map((opt, i) => ({
          ...opt,
          optionId: getAutoFillOptionKey(point.pointNumber, i)
        }));
      }
      points[pointIndex] = point;
      return { ...sec, pointsWithOptions: points };
    });
  };

  /** Reorder option: direction is 'up' or 'down' */
  const moveOption = (sectionIndex, pointIndex, optionIndex, direction) => {
    const delta = direction === 'up' ? -1 : 1;
    const newIndex = optionIndex + delta;
    updateSection(sectionIndex, (sec) => {
      const points = [...(sec.pointsWithOptions || [])];
      const point = { ...(points[pointIndex] || emptyPoint()) };
      const options = [...(point.options || [])];
      if (newIndex < 0 || newIndex >= options.length) return sec;
      [options[optionIndex], options[newIndex]] = [options[newIndex], options[optionIndex]];
      point.options = options;
      if (point.pointNumber) {
        point.options = point.options.map((opt, i) => ({
          ...opt,
          optionId: getAutoFillOptionKey(point.pointNumber, i)
        }));
      }
      points[pointIndex] = point;
      return { ...sec, pointsWithOptions: points };
    });
  };

  const handleTemplateChange = (e) => {
    const { name, value } = e.target;
    setTemplate(prev => ({ ...prev, [name]: value }));
  };

  const handleSectionFieldChange = (index, field, value) => {
    updateSection(index, (sec) => ({ ...sec, [field]: value }));
  };

  const handlePointFieldChange = (sectionIndex, pointIndex, field, value) => {
    updateSection(sectionIndex, (sec) => {
      const points = [...(sec.pointsWithOptions || [])];
      const point = { ...(points[pointIndex] || emptyPoint()) };
      point[field] = value;
      if (field === 'pointNumber' && (point.options || []).length > 0) {
        point.options = point.options.map((opt, i) => ({
          ...opt,
          optionId: getAutoFillOptionKey(value, i)
        }));
      }
      points[pointIndex] = point;
      return { ...sec, pointsWithOptions: points };
    });
  };

  const handleOptionFieldChange = (sectionIndex, pointIndex, optionIndex, field, value) => {
    if (field === 'optionId') return; // option keys are always auto-filled and read-only
    updateSection(sectionIndex, (sec) => {
      const points = [...(sec.pointsWithOptions || [])];
      const point = { ...(points[pointIndex] || emptyPoint()) };
      const options = [...(point.options || [])];
      const option = { ...(options[optionIndex] || emptyOption()) };
      option[field] = value;
      options[optionIndex] = option;
      point.options = options;
      points[pointIndex] = point;
      return { ...sec, pointsWithOptions: points };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const termsForApi = sections.map((sec) => ({
        ...sec,
        pointsWithOptions: (sec.pointsWithOptions || []).map(({ autoFillOptionKeys, ...p }) => p)
      }));
      const payload = {
        code: template.code,
        name: template.name || template.code,
        description: template.description || null,
        serviceId: template.serviceId ?? null,
        proposalTemplateId: template.proposalTemplateId ?? null,
        terms: termsForApi
      };
      await api.put(`/md/commercial-terms/templates/${id}`, payload);
      setSuccessMessage('Commercial terms template saved successfully.');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving commercial term template:', error);
      const msg = error.response?.data || error.message || 'Failed to save.';
      setFailMessage(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setShowFailModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/masterdata-commercial-terms');
  };

  return (
    <>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={handleBack}
                >
                  <ArrowLeft className="icon me-1" size={18} />
                  Back
                </button>
                <h2 className="page-title mb-0">Commercial Terms Detail</h2>
              </div>
              <div className="text-muted">
                Edit sections, points, and options for template <strong>{template.code}</strong>.
              </div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || loading}
              >
                <Save className="icon me-1" size={18} />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-12 mb-3">
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Code</label>
                        <input
                          type="text"
                          className="form-control"
                          value={template.code}
                          name="code"
                          onChange={handleTemplateChange}
                          placeholder="certification, training, iscc, ..."
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={template.name}
                          name="name"
                          onChange={handleTemplateChange}
                          placeholder="Display name"
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={template.description}
                          name="description"
                          onChange={handleTemplateChange}
                          placeholder="Optional description"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Service</label>
                        <select
                          className="form-select"
                          value={template.serviceId ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTemplate(prev => ({ ...prev, serviceId: v === '' ? null : Number(v) }));
                          }}
                        >
                          <option value="">— None —</option>
                          {services.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Proposal Template</label>
                        <select
                          className="form-select"
                          value={template.proposalTemplateId ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTemplate(prev => ({ ...prev, proposalTemplateId: v === '' ? null : Number(v) }));
                          }}
                        >
                          <option value="">— None —</option>
                          {proposalTemplateOptions.map((t) => (
                            <option key={t.id} value={t.id}>{t.templateName ?? t.name ?? t.id}</option>
                          ))}
                        </select>
                        <small className="text-muted">Link to a proposal template so this set appears when that template is selected in a proposal.</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                {sections.map((section, sIdx) => (
                  <div key={`section-${sIdx}`} className="card mb-3">
                    <div className="card-header d-flex align-items-center">
                      <div className="me-auto">
                        <strong>Section {section.sectionOrder}</strong>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => removeSection(sIdx)}
                        disabled={sections.length === 1}
                      >
                        Remove Section
                      </button>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-md-4">
                          <label className="form-label">Section Type</label>
                          <input
                            type="text"
                            className="form-control"
                            value={section.sectionType}
                            onChange={(e) => handleSectionFieldChange(sIdx, 'sectionType', e.target.value)}
                            placeholder="e.g. PAYMENT, VAT, INVOICE_GENERATION"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Section Title</label>
                          <input
                            type="text"
                            className="form-control"
                            value={section.sectionTitle}
                            onChange={(e) => handleSectionFieldChange(sIdx, 'sectionTitle', e.target.value)}
                            placeholder="e.g. Clause 4 - Payment(s)"
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">Order</label>
                          <input
                            type="number"
                            className="form-control"
                            value={section.sectionOrder}
                            onChange={(e) => handleSectionFieldChange(sIdx, 'sectionOrder', Number(e.target.value) || 0)}
                          />
                        </div>
                      </div>

                      <div className="alert alert-info py-2 px-3 mb-3 small">
                        <strong>Points:</strong> Set <strong>Number</strong>, <strong>Base Text</strong>, and <strong>Input type</strong>. Use <strong>RADIO</strong> for select/radio (add options below); use <strong>NUMBER</strong>, <strong>TEXT</strong>, or <strong>CURRENCY</strong> for a single input in the proposal. For NUMBER or CURRENCY you can set an optional <strong>Suffix</strong> (e.g. participants, or IDR for currency prefix).
                      </div>

                      <div className="mb-2 d-flex justify-content-between align-items-center">
                        <h4 className="card-title mb-0">Points</h4>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => addPoint(sIdx)}
                        >
                          Add Point
                        </button>
                      </div>

                      {(section.pointsWithOptions || []).length === 0 ? (
                        <div className="text-muted">No points yet. Click &quot;Add Point&quot;.</div>
                      ) : (
                        (section.pointsWithOptions || []).map((point, pIdx) => (
                          <div key={`point-${sIdx}-${pIdx}`} className="border rounded p-3 mb-3">
                            <div className="row mb-2">
                              <div className="col-md-2">
                                <label className="form-label">Number</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={point.pointNumber}
                                  onChange={(e) => handlePointFieldChange(sIdx, pIdx, 'pointNumber', e.target.value)}
                                  placeholder="e.g. 3.2"
                                />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label">Base Text</label>
                                <textarea
                                  className="form-control"
                                  rows={2}
                                  value={point.pointText}
                                  onChange={(e) => handlePointFieldChange(sIdx, pIdx, 'pointText', e.target.value)}
                                  placeholder="Base text for the point (before options)."
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label">Input type</label>
                                <select
                                  className="form-select"
                                  value={point.inputType || 'RADIO'}
                                  onChange={(e) => handlePointFieldChange(sIdx, pIdx, 'inputType', e.target.value)}
                                >
                                  {INPUT_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                  ))}
                                </select>
                              </div>
                              {['NUMBER', 'CURRENCY'].includes(point.inputType || '') && (
                                <div className="col-md-2">
                                  <label className="form-label">Suffix</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={point.inputSuffix || ''}
                                    onChange={(e) => handlePointFieldChange(sIdx, pIdx, 'inputSuffix', e.target.value)}
                                    placeholder={point.inputType === 'CURRENCY' ? 'e.g. IDR' : 'e.g. participants'}
                                  />
                                </div>
                              )}
                              <div className="col-md-2 d-flex align-items-end">
                                <button
                                  type="button"
                                  className="btn btn-outline-danger w-100"
                                  onClick={() => removePoint(sIdx, pIdx)}
                                >
                                  Remove Point
                                </button>
                              </div>
                            </div>

                            <div className="card mt-2">
                              <div className="card-header py-2 d-flex justify-content-between align-items-center">
                                <strong className="mb-0">Options</strong>
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() => addOptionToPoint(sIdx, pIdx)}
                                >
                                  Add Option
                                </button>
                              </div>
                              <div className="card-body">
                                {(point.options || []).length === 0 ? (
                                  <div className="text-muted">
                                    <strong>Input type:</strong> No options = this point will show as a single input (number/text) in the proposal. To make it a select or radio, click &quot;Add Option&quot; and add one or more choices.
                                  </div>
                                ) : (
                                  (point.options || []).map((opt, oIdx) => (
                                    <div key={`opt-${sIdx}-${pIdx}-${oIdx}`} className="row mb-2">
                                      <div className="col-md-3">
                                        <label className="form-label">Option ID</label>
                                        <input
                                          type="text"
                                          className="form-control bg-light"
                                          value={getAutoFillOptionKey(point.pointNumber, oIdx)}
                                          readOnly
                                          aria-label="Option ID (auto-filled from point number)"
                                        />
                                      </div>
                                      <div className="col-md-7">
                                        <label className="form-label">Option Text</label>
                                        <textarea
                                          className="form-control"
                                          rows={2}
                                          value={opt.optionText}
                                          onChange={(e) => handleOptionFieldChange(sIdx, pIdx, oIdx, 'optionText', e.target.value)}
                                          placeholder="Full option text"
                                        />
                                      </div>
                                      <div className="col-md-2 d-flex flex-column align-items-stretch gap-1 justify-content-end">
                                        <div className="btn-group">
                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            title="Move up"
                                            disabled={oIdx === 0}
                                            onClick={() => moveOption(sIdx, pIdx, oIdx, 'up')}
                                          >
                                            <ChevronUp size={18} />
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            title="Move down"
                                            disabled={oIdx === (point.options || []).length - 1}
                                            onClick={() => moveOption(sIdx, pIdx, oIdx, 'down')}
                                          >
                                            <ChevronDown size={18} />
                                          </button>
                                        </div>
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger w-100"
                                          onClick={() => removeOptionFromPoint(sIdx, pIdx, oIdx)}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={addSection}
                >
                  <Plus className="icon me-1" size={18} />
                  Add Section
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />
      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Error"
        message={failMessage}
        buttonText="OK"
      />
    </>
  );
};

export default CommercialTermDetail;

